import { Helmet } from 'react-helmet-async';

/**
 * SEO Head Component - Dynamic meta tags for each page
 * Supports OpenGraph, Twitter Card, canonical URL, noindex, and structured data.
 */
const SEOHead = ({ 
  title = "StoxCap.ai - India's AI-Powered Brokering Platform for Stock Trading and Investment",
  description = "Trade stocks, F&O, mutual funds, ETFs, bonds and more on StoxCap. India's fastest growing trading platform with AI-powered insights.",
  path = "",
  type = "website",
  image = "/logo512.png",
  noindex = false,
  structuredData = null
}) => {
  const baseUrl = "https://stoxcap.ai";
  const canonicalUrl = `${baseUrl}${path}`;
  const fullTitle = path === "/" || path === "" 
    ? "StoxCap.ai - India's AI-Powered Brokering Platform for Stock Trading and Investment" 
    : `${title} | StoxCap.ai`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      <link rel="canonical" href={canonicalUrl} />
      
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:site_name" content="StoxCap" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />
      
      {noindex ? (
        <>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </>
      ) : (
        <>
          <meta name="robots" content="index, follow" />
          <meta name="googlebot" content="index, follow" />
        </>
      )}
      
      <meta name="language" content="English" />
      <meta name="revisit-after" content="1 days" />
      <meta name="author" content="Ujjwal Jain Securities Ltd." />

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Page-specific SEO configurations
export const PageSEO = {
  home: {
    title: "StoxCap.ai - India's AI-Powered Brokering Platform for Stock Trading and Investment",
    description: "Trade stocks, F&O, mutual funds, ETFs, bonds on StoxCap. AI-powered insights, real-time market data. Start your investment journey today.",
    path: "/"
  },
  dashboard: {
    title: "Dashboard",
    description: "View your portfolio, track investments, see market movers and get personalized insights on StoxCap dashboard.",
    path: "/dashboard"
  },
  stocks: {
    title: "Stocks - NSE & BSE Listed Equities",
    description: "Explore and trade NSE & BSE listed stocks. Real-time prices, charts, and analysis for 2000+ Indian equities on StoxCap.",
    path: "/market/equity"
  },
  fno: {
    title: "F&O Trading - Futures & Options",
    description: "Trade Futures & Options on NIFTY, BANKNIFTY and 150+ F&O stocks. Option chain, Greeks, strategies, and AI-powered strategy builder.",
    path: "/market/fo"
  },
  commodities: {
    title: "Commodities - MCX Trading",
    description: "Trade commodities on MCX - Gold, Silver, Crude Oil, Natural Gas, Copper and more. Live prices and charts.",
    path: "/commodities"
  },
  forex: {
    title: "Forex - Currency Trading",
    description: "Trade currency pairs on NSE CDS - USDINR, EURINR, GBPINR, JPYINR. Live forex rates and charts.",
    path: "/market/forex"
  },
  mutualFunds: {
    title: "Mutual Funds - Invest in Top Funds",
    description: "Invest in top-rated mutual funds. SIP, lump sum investments across equity, debt, and hybrid funds on StoxCap.",
    path: "/market/mf"
  },
  etf: {
    title: "ETFs - Exchange Traded Funds",
    description: "Trade Exchange Traded Funds - Gold ETFs, Index ETFs, Sector ETFs with real-time NAV and prices on StoxCap.",
    path: "/market/etf"
  },
  bonds: {
    title: "Bonds - Fixed Income Investments",
    description: "Invest in Government Securities, Corporate Bonds, Tax-Free Bonds. Fixed income investments with assured returns.",
    path: "/bonds"
  },
  ipo: {
    title: "IPO - Upcoming & Listed IPOs",
    description: "Latest IPO listings, apply for new IPOs, track subscription status and allotment on StoxCap.",
    path: "/ipo"
  },
  sectors: {
    title: "Sectors - Industry Performance",
    description: "Track sector-wise performance of Indian stock market. View gainers, losers and trending sectors today.",
    path: "/sectors"
  },
  optionChain: {
    title: "Option Chain",
    description: "Live NSE option chain for NIFTY, BANKNIFTY, and F&O stocks. Strike prices, OI, Greeks analysis on StoxCap.",
    path: "/option-chain"
  },
  searchResults: {
    title: "Search Results",
    description: "Search across stocks, ETFs, mutual funds, commodities, and more on StoxCap.",
    path: "/search"
  },
  aiAdvisor: {
    title: "AI Advisor",
    description: "Get AI-powered investment advice, portfolio analysis, and market insights from StoxCap's AIR Advisor.",
    path: "/ai-advisor"
  },
  algoTrading: {
    title: "Algo Trading",
    description: "Build, backtest and deploy algorithmic trading strategies. Paper trade with automated systems on StoxCap.",
    path: "/algo-trading"
  },
  wallet: {
    title: "Wallet",
    description: "Manage your trading balance, view transactions, add funds on StoxCap.",
    path: "/wallet"
  },
  // Noindex pages
  login: {
    title: "Login",
    description: "Log in to your StoxCap account.",
    path: "/login",
    noindex: true
  },
  admin: {
    title: "Admin",
    description: "StoxCap admin dashboard.",
    path: "/admin",
    noindex: true
  },
  settings: {
    title: "Settings",
    description: "Manage your StoxCap account settings.",
    path: "/settings",
    noindex: true
  },
  onboarding: {
    title: "Onboarding",
    description: "Complete your StoxCap onboarding.",
    path: "/onboarding",
    noindex: true
  },
  opsDashboard: {
    title: "Ops Dashboard",
    description: "StoxCap operations dashboard.",
    path: "/dashboard/ops",
    noindex: true
  }
};

// Structured data for Organization + WebSite (search action)
export const SiteStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://stoxcap.ai/#organization",
      "name": "StoxCap",
      "url": "https://stoxcap.ai",
      "logo": {
        "@type": "ImageObject",
        "url": "https://stoxcap.ai/logo512.png"
      },
      "sameAs": []
    },
    {
      "@type": "WebSite",
      "@id": "https://stoxcap.ai/#website",
      "url": "https://stoxcap.ai",
      "name": "StoxCap",
      "publisher": { "@id": "https://stoxcap.ai/#organization" },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://stoxcap.ai/search?q={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    }
  ]
};

export default SEOHead;
